import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import type { EventInstance } from '@pacer/shared';
import * as api from '../api';
import { queryKeys } from '../state/queries';

/**
 * Everything renders in the HOUSEHOLD's timezone, never the browser's.
 *
 * That distinction matters: a parent checking the calendar from a hotel in another zone
 * must still see "soccer at 7pm", the same string the wall at home is showing.
 */
export function useZone(): string {
  const { data } = useQuery({ queryKey: queryKeys.household, queryFn: api.getHousehold });
  return data?.timezone ?? DateTime.local().zoneName ?? 'UTC';
}

export const dt = (iso: string, zone: string): DateTime => DateTime.fromISO(iso, { zone });

/** `7:00 PM` / `7:30 PM` — drops `:00` for whole hours to reduce noise on the wall. */
export function fmtTime(iso: string, zone: string): string {
  const d = dt(iso, zone);
  return d.minute === 0 ? d.toFormat('h a') : d.toFormat('h:mm a');
}

export function fmtRange(ev: EventInstance, zone: string): string {
  if (ev.allDay) return 'All day';
  const start = fmtTime(ev.startTs, zone);
  const end = fmtTime(ev.endTs, zone);
  return start === end ? start : `${start} – ${end}`;
}

/** `Today` / `Tomorrow` / `Yesterday` / `Wed, Jul 15` */
export function fmtDayLabel(date: DateTime, zone: string): string {
  const today = DateTime.now().setZone(zone).startOf('day');
  const diff = date.startOf('day').diff(today, 'days').days;
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  return date.toFormat('ccc, LLL d');
}

/** Inclusive list of the days an event touches — an all-day DTEND is exclusive. */
export function daysSpanned(ev: EventInstance, zone: string): string[] {
  const start = dt(ev.startTs, zone).startOf('day');
  let end = dt(ev.endTs, zone);
  end = ev.allDay ? end.minus({ millisecond: 1 }).startOf('day') : end.startOf('day');

  const out: string[] = [];
  for (let d = start; d <= end; d = d.plus({ days: 1 })) {
    const iso = d.toISODate();
    if (iso) out.push(iso);
    if (out.length > 366) break; // guard against a pathological span
  }
  return out;
}

/** The 6-week grid (always 42 cells) that a month view renders. */
export function monthGrid(month: DateTime): DateTime[] {
  const first = month.startOf('month').startOf('week', { useLocaleWeeks: true });
  return Array.from({ length: 42 }, (_, i) => first.plus({ days: i }));
}

/** Local `<input type="datetime-local">` value ⇄ real instant, both in the household zone. */
export function toLocalInput(iso: string, zone: string): string {
  return dt(iso, zone).toFormat("yyyy-LL-dd'T'HH:mm");
}

export function fromLocalInput(local: string, zone: string): string {
  return DateTime.fromISO(local, { zone }).toUTC().toISO() ?? new Date().toISOString();
}

export function toDateInput(iso: string, zone: string): string {
  return dt(iso, zone).toFormat('yyyy-LL-dd');
}

export function fromDateInput(date: string, zone: string): string {
  return DateTime.fromISO(date, { zone }).startOf('day').toUTC().toISO() ?? new Date().toISOString();
}
